import React from "react";
import { Link } from "react-router-dom";
import { FiBell, FiAlertTriangle, FiAlertCircle, FiShield, FiX, FiExternalLink, FiArrowRight } from "react-icons/fi";
import { normAnnPriority, etykietaWariantuPaska } from "../../utils/annPriority";
import { pierwszeLinieTekstu, maWiecejNizLinie } from "../../utils/tekstKomunikatu";
import styles from "./AnnouncementTopBar.module.css";

/**
 * WIDOK JEDNEGO PASKA KOMUNIKATU — sam wygląd, bez pobierania i bez stanu.
 *
 * [polecenie właściciela 2026-10-04] Ten sam komponent rysuje pasek nad nagłówkiem aplikacji i podgląd
 * w oknie edycji („Tak zobaczy to pracownik”). BYŁO: podgląd w edytorze był osobnym kawałkiem ze stylami
 * wpisanymi w kod, zawsze w kolorze akcentu — nie pokazywał wariantu (ostrzeżenie / pilne), odnośnika ani
 * tego, czy pasek da się zamknąć. Podgląd, który rysuje co innego niż aplikacja, nie jest podglądem.
 *
 * Priorytet przychodzi z API NAZWĄ enuma („Warning”) — normAnnPriority przyjmuje oba formaty.
 * Kolor niesie WYŁĄCZNIE klasa CSS (jedna zmienna --tb-color), żaden kolor nie jest składany w JS.
 */
const PRIORITY_LOOK = {
    1: { cls: "priorityWarning", Icon: FiAlertTriangle },
    2: { cls: "priorityCritical", Icon: FiAlertCircle },
    99: { cls: null, Icon: FiShield },
};
const lookOf = (priority) => PRIORITY_LOOK[normAnnPriority(priority)] || { cls: null, Icon: FiBell };

/** Ile linijek treści pokazuje pasek — liczba z polecenia właściciela (2026-09-17), trzymana w jednym miejscu. */
export const LINIJEK_W_PASKU = 2;

/** Napis odnośnika, gdy autor nie podał własnego. */
export const DOMYSLNY_NAPIS_ODNOSNIKA = "Zobacz więcej";

/**
 * Jaki to odnośnik: "sciezka" (ekran aplikacji), "https" (strona zewnętrzna) albo null (niedozwolony / brak).
 * Serwer pilnuje tego samego przy zapisie; tu druga zasuwa — pasek nigdy nie wyrenderuje `javascript:` ani `//host`,
 * nawet gdyby taki adres znalazł się w bazie inną drogą.
 */
export const rodzajOdnosnikaPaska = (url) => {
    const u = typeof url === "string" ? url.trim() : "";
    if (!u || /[\s\\]/.test(u)) return null;
    if (u.startsWith("/")) return u.startsWith("//") ? null : "sciezka";
    return /^https:\/\/[^/]+/i.test(u) ? "https" : null;
};

const PasekKomunikatuWidok = ({
    komunikat,
    podglad = false,
    pozycja,
    klasaDodatkowa = "",
    onOtworz,
    onZamknij,
    zamykanie = false,
    dodatek = null,
}) => {
    if (!komunikat) return null;
    const { cls, Icon } = lookOf(komunikat.priority);
    const linie = pierwszeLinieTekstu(komunikat.content, LINIJEK_W_PASKU);
    const jestWiecej = maWiecejNizLinie(komunikat.content, LINIJEK_W_PASKU);
    const zamykalny = komunikat.topBarDismissible !== false;
    const url = typeof komunikat.topBarLinkUrl === "string" ? komunikat.topBarLinkUrl.trim() : "";
    const rodzaj = rodzajOdnosnikaPaska(url);
    const napis = (komunikat.topBarLinkLabel || "").trim() || DOMYSLNY_NAPIS_ODNOSNIKA;

    const klasy = [styles.bar, cls ? styles[cls] : "", podglad ? styles.barPodglad : "", klasaDodatkowa]
        .filter(Boolean).join(" ");

    const wnetrze = (
        <>
            <span className={styles.naglowekRzad}>
                <span className={styles.kategoria} data-kategoria-komunikatu>
                    {etykietaWariantuPaska(komunikat.priority)}
                </span>
                {komunikat.title
                    ? <strong className={styles.title}>{komunikat.title}</strong>
                    : (podglad ? <strong className={styles.title}>(tytuł komunikatu)</strong> : null)}
            </span>
            {linie.length > 0 ? (
                <span className={styles.linie} data-linie-komunikatu={linie.length}>
                    {linie.map((linia, i) => (
                        <span key={i} className={styles.linia}>{linia}</span>
                    ))}
                </span>
            ) : (podglad ? <span className={styles.linie}><span className={styles.linia}>(tu stanie treść paska)</span></span> : null)}
            {!podglad && (
                <span className={styles.zacheta}>
                    {jestWiecej ? "Czytaj całość" : "Otwórz komunikat"}
                </span>
            )}
        </>
    );

    const zawartoscOdnosnika = (
        <>
            <span className={styles.odnosnikNapis}>{napis}</span>
            {rodzaj === "https"
                ? <FiExternalLink size={13} aria-hidden="true" />
                : <FiArrowRight size={13} aria-hidden="true" />}
        </>
    );

    let odnosnik = null;
    if (rodzaj && podglad) {
        // W podglądzie odnośnik jest nieczynny — autor ma zobaczyć, GDZIE stanie, a nie wyjść z okna edycji.
        odnosnik = <span className={styles.odnosnik} data-odnosnik-paska={rodzaj}>{zawartoscOdnosnika}</span>;
    } else if (rodzaj === "sciezka") {
        odnosnik = (
            <Link to={url} className={styles.odnosnik} data-odnosnik-paska="sciezka" aria-label={napis}>
                {zawartoscOdnosnika}
            </Link>
        );
    } else if (rodzaj === "https") {
        odnosnik = (
            <a href={url} target="_blank" rel="noopener noreferrer" className={styles.odnosnik}
                data-odnosnik-paska="https" aria-label={`${napis} (otwiera się w nowej karcie)`}>
                {zawartoscOdnosnika}
            </a>
        );
    }

    return (
        <div
            className={klasy}
            role={podglad ? undefined : "status"}
            data-pasek-komunikatu={komunikat.id ?? "podglad"}
            data-pasek-pozycja={pozycja}
        >
            <span className={styles.icon}><Icon size={13} aria-hidden="true" /></span>

            {/* Cały obszar treści jest PRZYCISKIEM — „opcja kliknięcia do otwarcia w oknie modal”.
                Odnośnik i ✕ stoją OBOK, nie w środku: przycisk w przycisku nie jest poprawnym HTML-em. */}
            {podglad ? (
                <span className={styles.text}>{wnetrze}</span>
            ) : (
                <button
                    type="button"
                    className={styles.text}
                    onClick={onOtworz}
                    data-otworz-komunikat={komunikat.id}
                    aria-label={`Otwórz pełną treść komunikatu: ${komunikat.title || "komunikat"}`}
                >
                    {wnetrze}
                </button>
            )}

            {odnosnik}
            {dodatek}
            {zamykalny && (podglad ? (
                <span className={styles.close} data-zamknij-paska aria-hidden="true"><FiX size={15} /></span>
            ) : (
                <button type="button" className={styles.close} onClick={onZamknij} aria-label="Zamknij komunikat"
                    disabled={zamykanie} data-zamknij-paska>
                    <FiX size={15} />
                </button>
            ))}
        </div>
    );
};

export default PasekKomunikatuWidok;
